import * as React from 'react';
import styled from 'styled-components';
import { excludeKeys, KeyStrokeListener } from '../../utils';
import { ICheckboxProps } from './checkbox-types';
import { CheckboxLabel, CustomCheckbox, CheckboxCheckedIcon, ChechkboxIntermediate } from './checkbox-parts';

interface ICheckboxState {
    checked: boolean | 'intermediate';
}

const CheckboxWrapper = styled.label.attrs(({ disabled }: Pick<ICheckboxProps, 'disabled'>) => ({
    disabled,
    className: String('ap-checkbox-wrapper')
}))`
    display: inline-block;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    user-select: none;
`;

const NativeCheckbox = styled.input.attrs({
    type: 'checkbox'
})`
    display: none;
`;

export class Checkbox extends React.PureComponent<ICheckboxProps, ICheckboxState> {
    private rootDOMElement: React.RefObject<HTMLDivElement>;
    private inputElement: React.RefObject<HTMLInputElement> = React.createRef();
    private keyStroke: KeyStrokeListener;
    static getDerivedStateFromProps(props: ICheckboxProps) {
        const { checked } = props;
        if (checked !== undefined) {
            return { checked };
        }
        return null;
    }

    constructor(props: ICheckboxProps) {
        super(props);
        const { checked, defaultChecked = false } = props;
        this.rootDOMElement = props.elementRef || React.createRef();
        this.keyStroke = new KeyStrokeListener(this.rootDOMElement);
        this.state = {
            checked: checked === undefined ? defaultChecked : checked
        };
    }

    componentDidMount() {
        this.keyStroke.subscribe(this._onSelectedKeyPress, ' ', 'Enter');
    }

    componentWillUnmount() {
        this.keyStroke.unsubscribe();
    }

    render() {
        const { className, disabled, hidden, label, title } = this.props;
        return hidden ? null : (
            <CheckboxWrapper className={className} disabled={disabled} title={title}>
                {this.renderCheckbox()}
                {this.renderNativeCheckbox()}
                {label && this.renderCheckboxLabel()}
            </CheckboxWrapper>
        );
    }

    private renderCheckbox() {
        const { disabled, error } = this.props;
        const { checked } = this.state;
        return (
            <CustomCheckbox ref={this.rootDOMElement} $disabled={disabled} $checked={checked} $error={error} tabIndex={disabled ? -1 : 0}>
                {
                    checked === true
                        ? (<CheckboxCheckedIcon $disabled={disabled} $checked={checked} $error={error} className="ap-checkbox-checkMark" iconName="ap-medium-checkmark.outline" iconSize="12" />)
                        : (checked === 'intermediate' ? <ChechkboxIntermediate $disabled={disabled} $checked={checked} $error={error} className="ap-checkbox-checkMark" /> : null)
                }
            </CustomCheckbox>
        );
    }

    private renderNativeCheckbox() {
        const { checked } = this.state;
        const checkboxProps = excludeKeys(this.props, 'className', 'label', 'children', 'onValueChange', 'checked', 'defaultChecked', 'elementRef', 'error');
        return <NativeCheckbox {...checkboxProps} ref={this.inputElement} checked={!!checked} onChange={this._onChange} />;
    }

    private renderCheckboxLabel() {
        const { label, required, disabled } = this.props;
        return (
            <CheckboxLabel $disabled={disabled} $required={required}>
                {label}
            </CheckboxLabel>
        );
    }

    private _onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { onValueChange, onChange, value = '' } = this.props;
        const { checked } = e.target;

        this.setState({ checked });

        if (onChange) {
            onChange(e);
        }

        if (onValueChange) {
            onValueChange(checked, value);
        }
    }

    private _onSelectedKeyPress = (e: any) => {
        const { onValueChange, onChange, value = '' } = this.props;
        const checked = !this.inputElement.current?.checked;

        this.setState({ checked });

        if (onChange) {
            onChange(e);
        }

        if (onValueChange) {
            onValueChange(checked, value);
        }
    }
}
